"use client";

import { authClient } from "@repo/auth/client";
import { config } from "@repo/config";
import { useSession } from "@saas/auth/hooks/use-session";
import { UserAvatar } from "@shared/components/UserAvatar";
import { useRouter } from "@shared/hooks/router";
import { clearCache } from "@shared/lib/cache";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@ui/components/dropdown-menu";
import { cn } from "@ui/lib";
import {
	CreditCard,
	GalleryHorizontalEnd,
	LogOutIcon,
	MoreVerticalIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname } from "next/navigation";

export function UserMenu({ showUserName }: { showUserName?: boolean }) {
	const t = useTranslations("subscription");
	const appT = useTranslations("app");
	const { user } = useSession();
	const pathname = usePathname();
	const router = useRouter();

	const onLogout = () => {
		authClient.signOut({
			fetchOptions: {
				onSuccess: async () => {
					await clearCache();
					window.location.href = new URL(
						config.auth.redirectAfterLogout,
						window.location.origin,
					).toString();
				},
			},
		});
	};

	// 获取订阅链接 - 不再使用returnUrl参数
	const getSubscriptionLink = () => {
		return "/app/subscription";
	};

	if (!user) {
		return null;
	}

	const { name, email, image } = user;

	return (
		<DropdownMenu modal={false}>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					className={cn(
						"flex items-center gap-2 rounded-md outline-hidden focus-visible:ring-2 focus-visible:ring-primary",
						showUserName
							? "w-full md:w-[100%+1rem] md:p-2"
							: "p-0.5",
						"hover:bg-primary/5",
					)}
					aria-label="User menu"
				>
					<span className="flex items-center gap-2">
						<UserAvatar name={name ?? ""} avatarUrl={image} />
						{showUserName && (
							<span className="text-left leading-tight">
								<span className="font-medium text-sm">
									{name}
								</span>
								<span className="block text-xs opacity-70">
									{email}
								</span>
							</span>
						)}
					</span>

					{showUserName && <MoreVerticalIcon className="size-4" />}
				</button>
			</DropdownMenuTrigger>

			<DropdownMenuContent align="end">
				<DropdownMenuLabel>
					{name}
					<span className="block font-normal text-xs opacity-70">
						{email}
					</span>
				</DropdownMenuLabel>

				<DropdownMenuSeparator />

				<DropdownMenuItem asChild>
					<Link href="/app/arts">
						<GalleryHorizontalEnd className="mr-2 size-4" />
						{t("tabs.myArts")}
					</Link>
				</DropdownMenuItem>

				<DropdownMenuItem asChild>
					<Link href={getSubscriptionLink()}>
						<CreditCard className="mr-2 size-4" />
						{t("tabs.mySubscription")}
					</Link>
				</DropdownMenuItem>

				<DropdownMenuSeparator />

				<DropdownMenuItem onClick={onLogout}>
					<LogOutIcon className="mr-2 size-4" />
					{appT("userMenu.logout")}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
